import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type Locale = "en" | "zh";

const en = {
  "brand.tagline": "Trusted knowledge supply chain",
  "nav.overview": "Overview",
  "nav.connections": "Connections",
  "nav.ingestion": "Ingestion",
  "nav.knowledge": "Knowledge",
  "nav.duplicates": "Duplicates",
  "nav.trust": "Trust policy",
  "nav.reviews": "Human review",
  "nav.adjudication": "Adjudication",
  "nav.calibration": "Calibration",
  "nav.search": "Trusted search",
  "nav.agents": "Agents",
  "nav.platform": "Platform",
  "nav.settings": "Settings",
  "nav.signOut": "Sign out",
  "nav.group.supply": "Supply",
  "nav.group.govern": "Govern",
  "nav.group.activate": "Activate",
  "nav.group.operate": "Operate",
  "knowledge.lineage.eyebrow": "End-to-end lineage",
  "knowledge.normalized.eyebrow": "Normalized content",
  "knowledge.governance.eyebrow": "Governance record",
  "knowledge.usage.eyebrow": "Usage evidence",
  "common.loading": "Loading",
  "common.retry": "Try again",
  "common.save": "Save changes",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.search": "Search",
  "common.create": "Create",
  "common.status": "Status",
  "common.updated": "Updated",
  "common.owner": "Owner",
  "common.actions": "Actions",
  "common.empty": "Nothing here yet",
  "common.back": "Back",
  "auth.signIn": "Sign in",
  "auth.createAccount": "Create account",
  "auth.email": "Work email",
  "auth.password": "Password",
  "auth.name": "Your name",
  "auth.workspace": "Workspace name",
  "auth.noAccount": "New to Assay?",
  "auth.hasAccount": "Already have an account?",
  "auth.eyebrow": "Enterprise knowledge infrastructure",
  "auth.loginDescription": "Continue to your trusted knowledge supply chain.",
  "auth.registerDescription":
    "Create a workspace and reach your first diagnosis in minutes.",
  "auth.passwordHint": "At least 12 characters",
  "auth.wait": "Please wait…",
  "auth.sessionAssurance": "Server-side secure sessions",
  "auth.isolationAssurance": "Tenant-isolated by design",
  "auth.visualLabel": "Assay knowledge supply chain",
  "auth.readOnly": "Read-only connections",
  "auth.noMigration": "No migration",
  "auth.fullLineage": "Full lineage",
  "auth.permissionAware": "Permission aware",
  "auth.welcome": "Make enterprise AI trustworthy.",
  "auth.description":
    "Connect knowledge in place, diagnose risk, resolve duplication and supply governed context to search and Agents.",
  "page.overview.title": "Knowledge supply overview",
  "page.connections.title": "Connect every source",
  "page.ingestion.title": "Ingestion timeline",
  "page.knowledge.title": "Knowledge inventory",
  "page.duplicates.title": "Duplicate clusters",
  "page.trust.title": "Trust policy & diagnostics",
  "page.reviews.title": "Human review",
  "page.adjudication.title": "Knowledge adjudication",
  "page.calibration.title": "Outcome calibration lab",
  "page.search.title": "Trusted search",
  "page.agents.title": "Enterprise Agents",
  "page.platform.title": "Platform operations",
  "page.settings.title": "Workspace settings",
} as const;

type MessageKey = keyof typeof en;

const zh: Record<MessageKey, string> = {
  "brand.tagline": "可信知识供应链",
  "nav.overview": "概览",
  "nav.connections": "连接器",
  "nav.ingestion": "摄取任务",
  "nav.knowledge": "知识库",
  "nav.duplicates": "重复治理",
  "nav.trust": "可信策略",
  "nav.reviews": "人工审核",
  "nav.adjudication": "知识裁决",
  "nav.calibration": "结果校准",
  "nav.search": "可信搜索",
  "nav.agents": "智能体",
  "nav.platform": "平台运营",
  "nav.settings": "设置",
  "nav.signOut": "退出登录",
  "nav.group.supply": "供应",
  "nav.group.govern": "治理",
  "nav.group.activate": "激活",
  "nav.group.operate": "运营",
  "knowledge.lineage.eyebrow": "端到端血缘",
  "knowledge.normalized.eyebrow": "规范化内容",
  "knowledge.governance.eyebrow": "治理记录",
  "knowledge.usage.eyebrow": "使用证据",
  "common.loading": "正在加载",
  "common.retry": "重新尝试",
  "common.save": "保存更改",
  "common.cancel": "取消",
  "common.close": "关闭",
  "common.search": "搜索",
  "common.create": "创建",
  "common.status": "状态",
  "common.updated": "更新时间",
  "common.owner": "负责人",
  "common.actions": "操作",
  "common.empty": "这里还没有内容",
  "common.back": "返回",
  "auth.signIn": "登录",
  "auth.createAccount": "创建账户",
  "auth.email": "工作邮箱",
  "auth.password": "密码",
  "auth.name": "你的姓名",
  "auth.workspace": "工作区名称",
  "auth.noAccount": "第一次使用 Assay？",
  "auth.hasAccount": "已经有账户？",
  "auth.eyebrow": "企业知识基础设施",
  "auth.loginDescription": "继续进入你的可信知识供应链。",
  "auth.registerDescription": "创建工作区，几分钟内完成第一次知识诊断。",
  "auth.passwordHint": "至少 12 个字符",
  "auth.wait": "请稍候…",
  "auth.sessionAssurance": "服务器端安全会话",
  "auth.isolationAssurance": "租户隔离设计",
  "auth.visualLabel": "Assay 知识供应链",
  "auth.readOnly": "只读连接",
  "auth.noMigration": "无需迁移",
  "auth.fullLineage": "完整血缘",
  "auth.permissionAware": "权限感知",
  "auth.welcome": "让企业 AI 值得信赖。",
  "auth.description":
    "原地连接企业知识，诊断风险、治理重复内容，并向搜索与智能体持续供应可信上下文。",
  "page.overview.title": "知识供应链概览",
  "page.connections.title": "连接全部知识源",
  "page.ingestion.title": "摄取任务时间线",
  "page.knowledge.title": "知识资产清单",
  "page.duplicates.title": "重复知识簇",
  "page.trust.title": "可信策略与诊断",
  "page.reviews.title": "人工审核",
  "page.adjudication.title": "知识裁决",
  "page.calibration.title": "结果校准实验室",
  "page.search.title": "可信搜索",
  "page.agents.title": "企业智能体",
  "page.platform.title": "平台运营",
  "page.settings.title": "工作区设置",
};

const messages: Record<Locale, Record<MessageKey, string>> = { en, zh };
const LOCALE_STORAGE_KEY = "assay.locale";
const PRODUCT_ROUTE_PREFIXES = [
  "/app",
  "/work",
  "/login",
  "/register",
  "/invite",
  "/accept-invitation",
] as const;

function isProductContentRoute(pathname: string): boolean {
  return PRODUCT_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function preferredLocale(): Locale {
  try {
    const saved = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
    if (saved === "zh" || saved === "en") return saved;
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
  return globalThis.navigator?.language?.toLowerCase().startsWith("zh")
    ? "zh"
    : "en";
}

function saveLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // The query parameter remains the authoritative fallback.
  }
}

export function translated(locale: Locale, key: MessageKey): string {
  return messages[locale][key];
}

export function pathWithLocale(path: string, locale: Locale): string {
  const url = new URL(path, "https://assay.local");
  url.searchParams.set("lang", locale);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function localeFromSearch(search: string, fallback: Locale = "en"): Locale {
  const value = new URLSearchParams(search).get("lang");
  return value === "zh" || value === "en" ? value : fallback;
}

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = localeFromSearch(location.search, preferredLocale());
  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    saveLocale(locale);
    const explicitLocale = new URLSearchParams(location.search).get("lang");
    if (
      explicitLocale !== "zh" &&
      explicitLocale !== "en" &&
      isProductContentRoute(location.pathname)
    ) {
      void navigate(
        pathWithLocale(`${location.pathname}${location.search}${location.hash}`, locale),
        { replace: true },
      );
    }
  }, [locale, location.hash, location.pathname, location.search, navigate]);
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale: (next) => {
        saveLocale(next);
        void navigate(
          pathWithLocale(`${location.pathname}${location.search}${location.hash}`, next),
          { replace: true },
        );
      },
      t: (key) => translated(locale, key),
    }),
    [locale, location.pathname, location.search, navigate],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
